import { SortDirections } from "@app/shared/waffles";
import { LocationConfidenceScoresLabelEnum, NetworkAssessmentAnalysisType } from "@app/projects/enumerators";
import { ServicingProviderResult } from "@app/projects/results/servicing-provider";
import NextClosestProviderResult from "@app/projects/results/servicing-provider/nextClosestProviderResult";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { EffectivenessEfficiencyValueEnum, MotiveScoreValueEnum, NewPatientClaimsEnum, CareJourneyScoresValueEnum } from "@app/projects/servicing-provider";
import { SpecialtyPredictionModelEnum } from "@app/projects/servicing-provider/provider-claims/SpecialtyPredictionModelEnum";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { NcpEnum } from "@app/projects/servicing-provider/types";
import ServiceBase from "@app/shared/serviceBase";

export default class NextClosestProviderService extends ServiceBase {
  public async requeueNextClosestProvider(projectId: number): Promise<void> {
    return await this.makePost(`nextClosestProvider/${projectId}/resubmit`);
  }

  /**
   * Retrieves the servicing providers for a given project, region, run-configuration, specialty-group-code & provider-network-type
   * @param projectId
   * @param regionSetId
   * @param runConfigurationId
   * @param specialtyGroupCode
   * @param networkAssessmentAnalysisType The type of analsysis for which to retreive results.
   * @param skip
   * @param take
   */
  public async nextClosestNetworkProviders(
    projectId: number,
    regionSetId: number,
    runConfigurationId: number,
    specialtyGroupCode: string,
    networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
    skip?: number,
    take?: number,
    sortedColumn?: string,
    sortDirection?: SortDirections,
    columnSubKey?: string,
    searchValue?: string,
    selectedEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    selectedEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    selectedVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    selectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    selectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    selectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    selectedNewPatientClaimsValues?: NewPatientClaimsEnum[],
    selectedTopProcedures?: string[],
    selectedCareJourneyCostValues?: CareJourneyScoresValueEnum[],
    selectedCareJourneyQualityValues?: CareJourneyScoresValueEnum[],
    selectedTopConditions?: string[],
    specialtyPredictionModelFilter?: SpecialtyPredictionModelEnum,
    selectedPgrConfidenceTiers?: number[],
  ): Promise<ServicingProviderResult> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode || !networkAssessmentAnalysisType) {
      return null;
    }

    if (!selectedVolumeLabels) {
      selectedVolumeLabels = [];
    }

    return await this.makePost<NextClosestProviderResult>(
      `nextClosestProvider/${projectId}/runconfigurations/${runConfigurationId}/regionsets/${regionSetId}/network`,
      {
        columnSubKey,
        networkAssessmentAnalysisType,
        searchValue,
        selectedCareJourneyCostValues,
        selectedCareJourneyQualityValues,
        selectedEffectivenessValues,
        selectedEfficiencyValues,
        selectedLineOfBusiness,
        selectedLocationConfidenceValues,
        selectedMotiveAppropriatenessValues,
        selectedNewPatientClaimsValues,
        selectedPgrConfidenceTiers,
        selectedTopConditions,
        selectedTopProcedures,
        selectedVolumeLabels,
        skip,
        sortDirection,
        sortedColumn,
        specialtyGroupCode,
        specialtyPredictionModelFilter,
        take,
      }
    );
  }

  /**
   * Retrieves the next closest prospect or market providers for a given project, region, run-configuration, specialty-group-code & provider-network-type
   */
  public async getNextClosestImpactProviders(
    projectId: number,
    regionSetId: number,
    runConfigurationId: number,
    specialtyGroupCode: string,
    networkAssessmentAnalysisType: NetworkAssessmentAnalysisType,
    providerType: ProviderNetworkTypeEnum,
    skip?: number,
    take?: number,
    sortedColumn?: string,
    sortDirection?: SortDirections,
    columnSubKey?: string,
    ncpFilterValue?: NcpEnum,
    excludedProviders?: boolean,
    searchValue?: string,
    selectedEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    selectedEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    selectedVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    selectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    selectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    selectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    selectedNewPatientClaimsValues?: NewPatientClaimsEnum[],
    selectedTopProcedures?: string[],
    selectedCareJourneyCostValues?: CareJourneyScoresValueEnum[],
    selectedCareJourneyQualityValues?: CareJourneyScoresValueEnum[],
    selectedTopConditions?: string[],
    specialtyPredictionModelFilter?: SpecialtyPredictionModelEnum,
    selectedPgrConfidenceTiers?: number[],
  ): Promise<NextClosestProviderResult> {
    // Return early if we don't have any of the good stuff
    if (!projectId || !regionSetId || !runConfigurationId || !specialtyGroupCode || !networkAssessmentAnalysisType) {
      return null;
    }

    return await this.makePost<NextClosestProviderResult>(
      `nextClosestProvider/${projectId}/runconfigurations/${runConfigurationId}/regionsets/${regionSetId}/specialtyGroupCodes/${encodeURIComponent(specialtyGroupCode)}/networkAssessmentAnalysisTypes/${networkAssessmentAnalysisType}/${providerType}`,
      {
        columnSubKey,
        excludedProviders,
        ncpFilterValue,
        searchValue,
        selectedCareJourneyCostValues,
        selectedCareJourneyQualityValues,
        selectedEffectivenessValues,
        selectedEfficiencyValues,
        selectedLineOfBusiness,
        selectedLocationConfidenceValues,
        selectedMotiveAppropriatenessValues,
        selectedNewPatientClaimsValues,
        selectedPgrConfidenceTiers,
        selectedTopConditions,
        selectedTopProcedures,
        selectedVolumeLabels,
        skip,
        sortDirection,
        sortedColumn,
        specialtyPredictionModelFilter,
        take,
      });
  }
}